import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, setAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authed
  const [error, setError] = useState("");

  const checkSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // If we're authenticated by cookie but sessionStorage is empty (e.g., the
      // cookie was set in a pre-v22 deploy before the Bearer fallback existed),
      // proactively pull a fresh access token so media <img> tags and multipart
      // uploads can use the ?token= query fallback on iOS WebKit.
      if (!getAccessToken()) {
        try {
          const refreshed = await api.post("/auth/refresh");
          if (refreshed.data?.access_token) setAccessToken(refreshed.data.access_token);
        } catch { /* already authed via cookie — nothing else to do */ }
      }
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Store the access token for the Bearer-fallback path used by iOS WebKit.
      if (data?.access_token) setAccessToken(data.access_token);
      // Keep user object clean of the token when exposing it to UI.
      const { access_token: _at, ...userData } = data || {};
      setUser(userData);
      return { ok: true };
    } catch (e) {
      let msg;
      if (e.response) {
        msg = formatApiErrorDetail(e.response.data?.detail) || `HTTP ${e.response.status}`;
      } else if (e.request) {
        msg = `Network error — cannot reach server. Check your connection or contact an admin (origin: ${typeof window !== "undefined" ? window.location.origin : "?"}).`;
      } else {
        msg = e.message || "Login failed";
      }
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAccessToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, error, refresh: checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
